import type { ProductDetail } from '@/api/models';
import { pickFromVariant, type BarcodeMatch } from './excelImportMeta';

/**
 * Matches file barcodes to existing catalog variants. The product-list
 * endpoint's `barcode` filter is a server-side substring match and list rows
 * don't carry variant barcodes, so matching is two-phase: list query per
 * unique barcode → candidate product ids → one detail fetch per unique
 * product → exact `variant.barcode` comparison.
 *
 * Fetchers are injected (the modal hook builds them from
 * `queryClient.ensureQueryData` + `apiClient.products`) so this module stays
 * pure and unit-testable. A failed fetch marks the barcode `'failed'` — the
 * row becomes doubtful instead of silently importing a restock as a new
 * product (which would create a duplicate catalog entry).
 */

export type BarcodeFetchers = {
    /** Candidate product ids whose variants substring-match the barcode. */
    findProductIdsByBarcode: (barcode: string) => Promise<string[]>;
    getProductDetail: (productId: string) => Promise<ProductDetail>;
};

/** Run `fn` over `items` with at most `limit` calls in flight. */
export const mapWithConcurrency = async <T, R>(
    items: readonly T[],
    limit: number,
    fn: (item: T) => Promise<R>,
): Promise<R[]> => {
    const results = new Array<R>(items.length);
    let next = 0;
    const worker = async (): Promise<void> => {
        while (next < items.length) {
            const index = next++;
            results[index] = await fn(items[index] as T);
        }
    };
    const workers = Array.from({ length: Math.min(limit, items.length) }, () =>
        worker(),
    );
    await Promise.all(workers);
    return results;
};

const LOOKUP_CONCURRENCY = 6;

/**
 * Resolve each unique barcode to an {@link BarcodeMatch}: an
 * `ExistingLinePick` when a variant's barcode matches exactly, `'failed'`
 * when a fetch involved in resolving it failed, or no entry when the catalog
 * simply has no such barcode (→ the row becomes a new product).
 */
export const matchBarcodes = async (
    barcodes: readonly string[],
    fetchers: BarcodeFetchers,
): Promise<Map<string, BarcodeMatch>> => {
    const unique = [
        ...new Set(barcodes.map((b) => b.trim()).filter((b) => b !== '')),
    ];

    // Phase 1 — candidate product ids per barcode.
    const candidates = await mapWithConcurrency(
        unique,
        LOOKUP_CONCURRENCY,
        async (barcode) => {
            try {
                return {
                    barcode,
                    productIds: await fetchers.findProductIdsByBarcode(barcode),
                };
            } catch {
                return { barcode, productIds: 'failed' as const };
            }
        },
    );

    // Phase 2 — one detail fetch per unique candidate product.
    const productIds = [
        ...new Set(
            candidates.flatMap((c) =>
                c.productIds === 'failed' ? [] : c.productIds,
            ),
        ),
    ];
    const details = new Map<string, ProductDetail | 'failed'>();
    await mapWithConcurrency(productIds, LOOKUP_CONCURRENCY, async (id) => {
        try {
            details.set(id, await fetchers.getProductDetail(id));
        } catch {
            details.set(id, 'failed');
        }
    });

    // Phase 3 — exact variant-barcode match per file barcode.
    const matches = new Map<string, BarcodeMatch>();
    for (const candidate of candidates) {
        if (candidate.productIds === 'failed') {
            matches.set(candidate.barcode, 'failed');
            continue;
        }
        let sawFailedDetail = false;
        let found = false;
        for (const productId of candidate.productIds) {
            const detail = details.get(productId);
            if (detail === 'failed' || detail === undefined) {
                sawFailedDetail = true;
                continue;
            }
            const variant = detail.variants.find(
                (v) => v.barcode === candidate.barcode,
            );
            if (variant) {
                matches.set(
                    candidate.barcode,
                    pickFromVariant(detail, variant),
                );
                found = true;
                break;
            }
        }
        // No exact match, but a candidate we couldn't inspect failed — we
        // can't rule out a restock, so flag instead of defaulting to 'new'.
        if (!found && sawFailedDetail) {
            matches.set(candidate.barcode, 'failed');
        }
    }
    return matches;
};
